import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Edit, Trash2, Cog, Search, RefreshCw, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Machines = () => {
  const navigate = useNavigate();
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingMachine, setEditingMachine] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [formData, setFormData] = useState({ id: "", name: "" });
  const [formLoading, setFormLoading] = useState(false);

  useEffect(() => { fetchMachines(); }, []);

  const fetchMachines = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/machines`);
      setMachines(res.data);
    } catch (err) {
      toast.error("Failed to fetch machines");
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchMachines();
    setRefreshing(false);
    toast.success("Refreshed");
  };

  const handleAddMachine = () => {
    setEditingMachine(null);
    setFormData({ id: "", name: "" });
    setShowForm(true);
  };

  const handleEditMachine = (machine) => {
    setEditingMachine(machine);
    setFormData({ id: machine.id, name: machine.name });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!formData.id.trim() || !formData.name.trim()) {
      toast.error("Machine ID and Name are required");
      return;
    }
    setFormLoading(true);
    try {
      if (editingMachine) {
        await axios.put(`${API}/machines/${editingMachine.id}`, formData);
        toast.success("Machine updated");
      } else {
        await axios.post(`${API}/machines`, formData);
        toast.success("Machine added");
      }
      setShowForm(false);
      fetchMachines();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setFormLoading(false);
    }
  };

  const confirmDelete = (id) => setDeleteId(id);

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/machines/${deleteId}`);
      toast.success("Machine deleted");
      fetchMachines();
    } catch (err) {
      toast.error("Failed to delete");
    } finally {
      setDeleteId(null);
    }
  };

  const filtered = machines.filter((m) => {
    if (!searchTerm) return true;
    const t = searchTerm.toLowerCase();
    return m.id?.toLowerCase().includes(t) || m.name?.toLowerCase().includes(t);
  });

  return (
    <div className="min-h-[calc(100vh-64px)] bg-gradient-to-br from-gray-900 via-slate-900 to-gray-900 p-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div className="flex items-center gap-4">
            <Button onClick={() => navigate("/workorder")} variant="ghost" className="text-gray-400 hover:text-white" data-testid="back-to-workorder">
              <ArrowLeft className="h-5 w-5 mr-2" /> Back
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-cyan-400 flex items-center gap-3">
                <Cog className="h-8 w-8" />
                Machine Management
              </h1>
              <p className="text-gray-400 mt-1">Manage production machines</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleRefresh} variant="outline" className="border-gray-600 text-gray-300 hover:bg-gray-800" disabled={refreshing}>
              <RefreshCw className={cn("h-4 w-4 mr-2", refreshing && "animate-spin")} /> Refresh
            </Button>
            <Button data-testid="add-machine-button" onClick={handleAddMachine} className="bg-cyan-600 hover:bg-cyan-700 text-white">
              <Plus className="h-4 w-4 mr-2" /> Add Machine
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          <Card className="bg-gray-800/50 border border-cyan-500/20">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="p-3 bg-cyan-500/20 rounded-lg"><Cog className="h-8 w-8 text-cyan-400" /></div>
              <div>
                <p className="text-3xl font-bold text-white">{machines.length}</p>
                <p className="text-sm text-gray-400">Total Machines</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gray-800/50 border border-green-500/20">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="p-3 bg-green-500/20 rounded-lg"><Cog className="h-8 w-8 text-green-400" /></div>
              <div>
                <p className="text-3xl font-bold text-white">{machines.length}</p>
                <p className="text-sm text-gray-400">Active</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search & Table */}
        <Card className="bg-gray-800/50 border-gray-700">
          <CardHeader className="border-b border-gray-700">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg text-cyan-400">Machines ({filtered.length})</CardTitle>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search..." className="bg-gray-700 border-gray-600 text-white pl-10 w-64" data-testid="machine-search" />
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-4">
            {loading ? (
              <p className="text-center text-gray-400 py-8">Loading...</p>
            ) : filtered.length === 0 ? (
              <p className="text-center text-gray-400 py-8">No machines found</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-gray-700 hover:bg-transparent">
                    <TableHead className="text-gray-300">Machine ID</TableHead>
                    <TableHead className="text-gray-300">Machine Name</TableHead>
                    <TableHead className="text-gray-300 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((machine) => (
                    <TableRow key={machine.id} className="border-gray-700 hover:bg-gray-700/30">
                      <TableCell>
                        <span className="inline-flex items-center px-3 py-1 rounded-md text-sm font-mono font-bold bg-cyan-500/10 text-cyan-400 border border-cyan-500/30">
                          {machine.id}
                        </span>
                      </TableCell>
                      <TableCell className="text-white font-medium">{machine.name}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button data-testid={`edit-button-${machine.id}`} variant="outline" size="sm" onClick={() => handleEditMachine(machine)} className="border-blue-500/50 text-blue-400 hover:bg-blue-500/20">
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button data-testid={`delete-button-${machine.id}`} variant="outline" size="sm" onClick={() => confirmDelete(machine.id)} className="border-red-500/50 text-red-400 hover:bg-red-500/20">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="bg-gray-900 border-cyan-500/50 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl text-cyan-400">{editingMachine ? "Edit Machine" : "Add Machine"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-gray-300">Machine ID *</Label>
              <Input value={formData.id} onChange={(e) => setFormData((p) => ({ ...p, id: e.target.value }))} className="bg-gray-800 border-gray-600 text-white mt-1" readOnly={!!editingMachine} data-testid="machine-id-input" />
            </div>
            <div>
              <Label className="text-gray-300">Machine Name *</Label>
              <Input value={formData.name} onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))} className="bg-gray-800 border-gray-600 text-white mt-1" data-testid="machine-name-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)} className="border-gray-600 text-gray-300">Cancel</Button>
            <Button onClick={handleSave} disabled={formLoading} className="bg-cyan-600 hover:bg-cyan-700" data-testid="machine-save-btn">
              {formLoading ? "Saving..." : editingMachine ? "Update" : "Add"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="bg-gray-900 border-red-500/50 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-400">Delete Machine</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">Are you sure? This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-button" className="border-gray-600 text-gray-300 hover:bg-gray-800">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-confirm-button" onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Machines;
